import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { WhatsAppButton } from "@/components/WhatsAppButton";
import { CookieConsent } from "@/components/CookieConsent";
import {
  CinematicHero,
  SectionHead,
  Reveal,
  FullBleedQuote,
  SplitFeature,
  StatStripe,
  CinematicCTA,
  CinematicFAQ,
  TripleStrip,
} from "@/components/service/Cinematic";
import heroImg from "@/assets/hero-house.jpg";
import aerial from "@/assets/hero-houses-aerial.jpg";
import ref1 from "@/assets/facade-ref-4.jpg";
import ref2 from "@/assets/facade-ref-5.jpg";
import ref3 from "@/assets/facade-ref-8.jpg";
import ref4 from "@/assets/facade-ref-9.jpeg";
import ref5 from "@/assets/facade-ref-10.jpeg";

const foerderungen = [
  {
    title: "KfW BEG EM",
    subtitle: "Bundesförderung effiziente Gebäude – Einzelmaßnahme",
    zuschuss: "bis 20 %",
    max: "max. 60.000 € förderfähig",
    fuer: "Außen- und Innendämmung, Dachboden",
    hinweis: "Energieberater erforderlich",
  },
  {
    title: "BAFA",
    subtitle: "Bundesamt für Wirtschaft und Ausfuhrkontrolle",
    zuschuss: "bis 15 %",
    max: "für Einzelmaßnahmen",
    fuer: "Gebäudehülle, Heizung, Lüftung",
    hinweis: "Antrag vor Auftragsvergabe",
  },
  {
    title: "§ 35c EStG",
    subtitle: "Steuerliche Absetzbarkeit",
    zuschuss: "20 % über 3 Jahre",
    max: "max. 40.000 € absetzbar",
    fuer: "Selbstnutzende Eigentümer",
    hinweis: "Bescheinigung durch Fachbetrieb",
  },
];

const faqs = [
  { q: "Was kostet eine energetische Sanierung in Herford?", a: "Eine Komplettsanierung (Fassade + Keller + Dach) kostet für ein typisches Einfamilienhaus 25.000–60.000 €. Einzelmaßnahmen: Fassade 18.000–35.000 €, Kellerdecke 3.000–7.000 €, Dachboden 2.000–5.000 €. Durch KfW-Förderung reduziert sich die Nettobelastung um 15–20 %." },
  { q: "Brauche ich einen Energieberater für die Förderung?", a: "Für die KfW BEG ist ein zugelassener Energieeffizienz-Experte (EEE) zwingend erforderlich. Wir arbeiten mit qualifizierten Beratern zusammen und vermitteln Sie weiter. Die Beraterkosten sind ebenfalls förderfähig." },
  { q: "Wann muss der Förderantrag gestellt werden?", a: "Vor Auftragsvergabe. Erst Förderantrag, dann Vertrag. Wir achten im Beratungsgespräch darauf, dass die Reihenfolge stimmt und keine Fördergelder verloren gehen." },
  { q: "Welche Sanierungsmaßnahme lohnt sich zuerst?", a: "Meist die Fassade – sie hat den größten Hebel auf den Energieverbrauch. Bei Schimmel oder kalten Wänden hat Innendämmung Priorität. Wir analysieren das Gebäude vor Ort und sortieren die Maßnahmen nach Wirkung pro Euro." },
];

const structuredData = {
  "@context": "https://schema.org",
  "@type": "Service",
  serviceType: "Energetische Sanierung",
  name: "Energetische Sanierung Herford",
  provider: { "@type": "LocalBusiness", name: "Malerfachbetrieb Stein GmbH & Co. KG" },
  areaServed: "Herford",
  description: "Energetische Sanierung in Herford – WDVS, Innendämmung und Fassade aus einer Hand.",
};

const EnergetischeSanierungHerford = () => (
  <div className="min-h-screen bg-background">
    <SEO
      title="Energetische Sanierung in Herford: Bis 20% Förderung"
      description="Energetische Sanierung in Herford: WDVS, Innendämmung & Fassade aus einer Hand. Bis zu 20 % KfW-/BAFA-Förderung sichern, Heizkosten dauerhaft senken. ☎ 05221 3832130"
      keywords="energetische Sanierung Herford, KfW Förderung Herford, BAFA Förderung Herford, Altbausanierung Herford, Energieberatung Herford, Heizkosten senken, Sanierungsfahrplan"
      canonical="https://malerfachbetrieb-stein.de/energetische-sanierung-herford"
      structuredData={structuredData}
    />
    <Navigation />
    <WhatsAppButton />

    <main>
      <CinematicHero
        chapter="Kapitel 02 / Energie"
        eyebrow="Energetische Sanierung"
        title={<>Heizkosten <em className="not-italic text-background/70">senken</em>. Substanz erhalten.</>}
        subtitle="Steigende Energiepreise, alte Bausubstanz, Schimmelprobleme – wir kennen die Altbauten Herfords. Planung, Dämmung und Sanierung von Fassade bis Dach aus einer Hand."
        image={heroImg}
        imageAlt="Energetische Sanierung Herford – Einfamilienhaus mit WDVS"
        ctaLabel="Energieberatung anfragen"
      />

      <StatStripe
        stats={[
          { value: "−30 %", label: "Heizenergie" },
          { value: "20 %", label: "KfW-Zuschuss" },
          { value: "40 J.", label: "Lebensdauer WDVS" },
          { value: "24h", label: "Rückmeldung" },
        ]}
      />

      <FullBleedQuote
        image={aerial}
        imageAlt="Wohnhäuser aus der Luft – Herford"
        quote={<>„Eine sanierte Hülle ist die effizienteste Investition, die ein Altbau zurückzahlen kann.“</>}
        caption="Energetische Sanierung – Praxisleitfaden"
      />

      {/* Vorteile */}
      <section className="py-16 md:py-24 lg:py-32">
        <div className="container mx-auto">
          <SectionHead
            eyebrow="Drei Gründe"
            title="Warum sich energetische Sanierung immer rechnet."
          />
          <div className="space-y-24 md:space-y-32">
            <SplitFeature
              index="01"
              eyebrow="Verbrauch"
              title="Bis zu 30 % weniger Heizkosten."
              body="Eine vollständige Sanierung von Fassade, Keller und Dach reduziert den Heizwärmebedarf eines typischen 70er- oder 80er-Jahre-Einfamilienhauses um 20–40 %. Bei 3.000 € Jahresverbrauch sind das bis zu 1.200 € Ersparnis – jedes Jahr."
              image={ref1}
              imageAlt="Einfamilienhaus mit Fassadendämmung Herford-Diebrock"
            />
            <SplitFeature
              reverse
              index="02"
              eyebrow="Substanz"
              title="Wertsteigerung statt Wertverlust."
              body="Energieeffizienz ist zentrales Kaufkriterium. Gebäude mit Energieausweis A/B erzielen bei Verkauf und Vermietung deutlich bessere Preise. Energetische Sanierung ist Wertanlage – nicht Kostenfaktor."
              image={ref2}
              imageAlt="Komplettsaniertes Altbau-Mehrfamilienhaus"
            />
            <SplitFeature
              index="03"
              eyebrow="Förderung"
              title="Bis zu 20 % vom Staat zurück."
              body="Bund und Land fördern Sanierungen umfangreich: KfW BEG, BAFA, steuerliche Absetzbarkeit. Wir begleiten Sie durch den Antragsprozess und stellen die nötigen Bescheinigungen aus."
              image={ref3}
              imageAlt="Energetisch saniertes Reihenhaus"
            />
          </div>
        </div>
      </section>

      {/* Förderungen */}
      <section className="py-16 md:py-24 lg:py-32 bg-foreground text-background">
        <div className="container mx-auto">
          <SectionHead
            invert
            eyebrow="Förderungen 2025"
            title="Drei Wege zu spürbarer Entlastung."
            intro="Förderkonditionen verändern sich regelmäßig – im Beratungsgespräch prüfen wir die aktuell gültigen Programme für Ihr Projekt."
          />
          <div className="grid md:grid-cols-3 gap-px bg-background/10">
            {foerderungen.map((f, i) => (
              <Reveal key={f.title} delay={i * 80}>
                <div className="bg-foreground p-8 md:p-10 h-full">
                  <div className="font-caslon text-5xl text-background/20 mb-6">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <h3 className="font-caslon text-2xl font-bold text-background mb-1">{f.title}</h3>
                  <p className="text-xs text-background/80 mb-6">{f.subtitle}</p>
                  <div className="space-y-4 text-sm border-t border-background/15 pt-5">
                    <div>
                      <div className="text-[10px] tracking-[0.25em] uppercase text-background/75 mb-1">Zuschuss</div>
                      <div className="font-caslon text-3xl font-bold text-background">{f.zuschuss}</div>
                      <div className="text-background/55 text-xs mt-1">{f.max}</div>
                    </div>
                    <div>
                      <div className="text-[10px] tracking-[0.25em] uppercase text-background/75 mb-1">Geeignet für</div>
                      <div className="text-background/80 text-sm">{f.fuer}</div>
                    </div>
                    <div className="pt-3 border-t border-background/15 text-xs text-background/55 italic">
                      {f.hinweis}
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <TripleStrip
        images={[
          { src: ref1, alt: "EFH Herford-Diebrock", caption: "EFH Herford-Diebrock" },
          { src: ref4, alt: "EFH Löhne – Vorderansicht", caption: "EFH Löhne – Vorderansicht" },
          { src: ref5, alt: "EFH Löhne – Detail", caption: "EFH Löhne – Detail" },
        ]}
      />

      <CinematicFAQ
        faqs={faqs}
        title="Fragen rund um Förderung & Ablauf."
      />

      <CinematicCTA
        image={aerial}
        imageAlt="Energetische Sanierung Herford"
        title={<>Energiepotenzial Ihres Hauses<br /> kennenlernen.</>}
        body="Vor-Ort-Beratung, unverbindlich – innerhalb 24 Stunden Rückmeldung."
        ctaLabel="Energieberatung anfragen"
      />
    </main>

    <Footer />
    <CookieConsent />
  </div>
);

export default EnergetischeSanierungHerford;